import { useEffect, useMemo, useRef, useState, type CSSProperties, type SyntheticEvent } from "react";
import type { VisualState } from "../services/conversation/state-machine";
import type { PlayableAsset } from "../services/media/types";

interface RemoteCharacterVideoProps {
  asset: PlayableAsset | null;
  state: VisualState;
  displayName: string;
  mouthLevel?: number;
  onStateVisible?: (state: VisualState) => void;
}

type VideoAsset = Extract<PlayableAsset, { kind: "video" }>;

type VideoLayer = {
  id: string;
  asset: VideoAsset;
  state: VisualState;
  phase: "active" | "incoming" | "ready" | "outgoing";
};

type VideoFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
};

const decodedFrameFallbackMs = 120;
const layerCleanupDelayMs = 260;

export function RemoteCharacterVideo({
  asset,
  state,
  displayName,
  mouthLevel = 0,
  onStateVisible
}: RemoteCharacterVideoProps) {
  const mediaRef = useRef<HTMLDivElement | null>(null);
  const cleanupTimer = useRef<number | null>(null);
  const transitionFrame = useRef<number | null>(null);
  const pendingReady = useRef<Set<string>>(new Set());
  const layersRef = useRef<VideoLayer[]>([]);
  const lastReportedState = useRef<VisualState | null>(null);
  const decodedFallbackTimers = useRef<Map<string, number>>(new Map());
  const videoLayer = useMemo(() => {
    if (asset?.kind !== "video") return null;
    return {
      id: `${state}:${asset.src}:${asset.fallback ?? ""}`,
      asset,
      state
    };
  }, [asset, state]);
  const [layers, setLayers] = useState<VideoLayer[]>(() =>
    videoLayer ? [{ ...videoLayer, phase: "active" }] : []
  );
  const isSpeakingState = state.startsWith("speaking");
  const normalizedMouthLevel = isSpeakingState ? Math.min(1, Math.max(0, mouthLevel)) : 0;
  const mouthStyle = {
    "--mouth-scale-x": (0.72 + normalizedMouthLevel * 0.42).toFixed(3),
    "--mouth-scale-y": (0.12 + normalizedMouthLevel * 1.58).toFixed(3),
    "--mouth-opacity": (0.04 + normalizedMouthLevel * 0.48).toFixed(3)
  } as CSSProperties;

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    const visibleLayer =
      layers.find((layer) => layer.phase === "ready") ??
      layers.find((layer) => layer.phase === "active");
    if (!visibleLayer || lastReportedState.current === visibleLayer.state) return;

    lastReportedState.current = visibleLayer.state;
    onStateVisible?.(visibleLayer.state);
  }, [layers, onStateVisible]);

  useEffect(() => {
    if (!videoLayer) {
      setLayers([]);
      return;
    }

    setLayers((current) => {
      const visibleLayer =
        current.find((layer) => layer.phase === "active" || layer.phase === "ready") ??
        current.find((layer) => layer.phase === "outgoing");
      if (visibleLayer?.id === videoLayer.id) return current;
      if (!visibleLayer) return [{ ...videoLayer, phase: "active" }];

      return [
        {
          ...visibleLayer,
          phase: "active"
        },
        {
          ...videoLayer,
          phase: "incoming"
        }
      ];
    });
  }, [videoLayer]);

  useEffect(() => {
    return () => {
      if (cleanupTimer.current) window.clearTimeout(cleanupTimer.current);
      if (transitionFrame.current) window.cancelAnimationFrame(transitionFrame.current);
      decodedFallbackTimers.current.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    const video = mediaRef.current?.querySelector<HTMLVideoElement>(
      ".remote-video-layer.is-active, .remote-video-layer.is-ready"
    );
    if (!video) return;

    video.playbackRate = isSpeakingState ? 0.86 + normalizedMouthLevel * 0.34 : 1;

    return () => {
      video.playbackRate = 1;
    };
  }, [isSpeakingState, layers, normalizedMouthLevel]);

  function activateLayer(layerId: string, event: SyntheticEvent<HTMLVideoElement>) {
    const target = layersRef.current.find((layer) => layer.id === layerId);
    if (target?.phase !== "incoming") return;
    if (pendingReady.current.has(layerId)) return;
    pendingReady.current.add(layerId);

    const video = event.currentTarget;
    video.currentTime = 0;
    void video.play();

    waitForDecodedFrame(layerId, video, () => {
      revealLayer(layerId);
    });
  }

  function waitForDecodedFrame(layerId: string, video: HTMLVideoElement, onReady: () => void) {
    let isReady = false;
    const finish = () => {
      if (isReady) return;
      isReady = true;
      const fallbackTimer = decodedFallbackTimers.current.get(layerId);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      decodedFallbackTimers.current.delete(layerId);
      onReady();
    };

    const frameVideo = video as VideoFrameCallback;
    if (frameVideo.requestVideoFrameCallback) {
      frameVideo.requestVideoFrameCallback(() => finish());
    } else {
      transitionFrame.current = window.requestAnimationFrame(() => {
        transitionFrame.current = window.requestAnimationFrame(() => finish());
      });
    }

    const fallbackTimer = window.setTimeout(finish, decodedFrameFallbackMs);
    decodedFallbackTimers.current.set(layerId, fallbackTimer);
  }

  function revealLayer(layerId: string) {
    if (transitionFrame.current) window.cancelAnimationFrame(transitionFrame.current);
    transitionFrame.current = window.requestAnimationFrame(() => {
      transitionFrame.current = window.requestAnimationFrame(() => {
        pendingReady.current.delete(layerId);
        layersRef.current = layersRef.current.map((layer) => {
          if (layer.id === layerId) return { ...layer, phase: "ready" };
          if (layer.phase === "active" || layer.phase === "ready") return { ...layer, phase: "outgoing" };
          return layer;
        });

        setLayers((current) => {
          const target = current.find((layer) => layer.id === layerId);
          if (target?.phase !== "incoming") return current;

          return current.map((layer) => {
            if (layer.id === layerId) return { ...layer, phase: "ready" };
            if (layer.phase === "active" || layer.phase === "ready") return { ...layer, phase: "outgoing" };
            return layer;
          });
        });

        if (cleanupTimer.current) window.clearTimeout(cleanupTimer.current);
        cleanupTimer.current = window.setTimeout(() => {
          completeLayer(layerId);
        }, layerCleanupDelayMs);
      });
    });
  }

  function completeLayer(layerId: string) {
    if (cleanupTimer.current) window.clearTimeout(cleanupTimer.current);
    setLayers((current) => {
      const readyLayer = current.find((layer) => layer.id === layerId && layer.phase === "ready");
      if (!readyLayer) return current.filter((layer) => layer.phase !== "outgoing");
      return [{ ...readyLayer, phase: "active" }];
    });
  }

  return (
    <div className="remote-frame">
      <div
        ref={mediaRef}
        className="remote-media"
        data-state={state}
        data-transitioning={layers.length > 1}
        data-talking={normalizedMouthLevel > 0.08}
        style={mouthStyle}
      >
        {layers.length > 0 ? (
          layers.map((layer) => (
            <video
              key={layer.id}
              className={`remote-video-layer is-${layer.phase}`}
              data-layer-state={layer.state}
              autoPlay
              muted
              loop={layer.asset.loop}
              playsInline
              preload="auto"
              onLoadedData={(event) => activateLayer(layer.id, event)}
              onCanPlay={(event) => activateLayer(layer.id, event)}
              onAnimationEnd={() => {
                if (layer.phase === "ready") completeLayer(layer.id);
              }}
            >
              <source src={layer.asset.src} type="video/webm" />
              {layer.asset.fallback ? <source src={layer.asset.fallback} type="video/mp4" /> : null}
            </video>
          ))
        ) : (
          <img src={asset?.src || "/assets/characters/neko-gamer/source/reference.jpeg"} alt={displayName} />
        )}
        <span className="lip-sync-mouth" aria-hidden="true" />
      </div>
      <div className="remote-overlay">
        <div>
          <span className="remote-name">{displayName}</span>
          <span className="remote-state" data-state={state}>
            {state}
          </span>
        </div>
        <span className="signal-bars" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </div>
    </div>
  );
}
